import type { Entry } from "../types";

export const systemGuideMessages = {
  sysGuideOpen: { ko: "시스템 안내", en: "How this system works" },
  sysGuideTitle: { ko: "이 시스템은 이렇게 동작합니다", en: "How this system works" },
  sysGuideIntro: {
    ko: "검토 기준을 세우고, 광고를 그 기준에 대조하고, 결과를 운영하고 감사하는 세 단계로 이어집니다. 상자를 누르면 해당 메뉴로 이동해 화면에서 위치를 알려 드립니다.",
    en: "Standards are defined, advertising is checked against them, and the outcome is operated and audited. Select any box to open that menu and see where it sits in the menu.",
  },
  sysGuideStageStandards: { ko: "1. 기준 수립", en: "1. Define standards" },
  // Neutral on purpose: this stage is now visible to requesters, who can read
  // every page in it but define nothing.
  sysGuideStageStandardsDesc: {
    ko: "검토가 무엇을 근거로 판단되는지입니다.",
    en: "What reviews are judged against.",
  },
  sysGuideStageReview: { ko: "2. 검토 실행", en: "2. Run the review" },
  sysGuideStageReviewDesc: {
    ko: "제출된 광고를 그 기준에 대조합니다.",
    en: "Check submitted material against those standards.",
  },
  sysGuideStageOperations: { ko: "3. 운영과 감사", en: "3. Operate and audit" },
  sysGuideStageOperationsDesc: {
    ko: "검토가 동작하는 방식과 남는 기록을 관리합니다.",
    en: "Manage how reviews run and what is recorded.",
  },
  sysGuideSourcesGroup: { ko: "근거 자료", en: "Source material" },
  sysGuideFlowScope: {
    ko: "채널과 구분으로 연결",
    en: "Mapped by channel and category",
  },
  sysGuideFlowSnapshot: {
    ko: "제출 시점의 기준을 스냅샷으로 고정",
    en: "Standards are snapshotted at submission",
  },
  sysGuideNodePoliciesDesc: {
    ko: "법령, 감독기관 가이드, 내부 정책 원본을 등록하고 버전으로 관리합니다.",
    en: "Register regulations, authority guidance, and internal policy documents, and keep them as versions.",
  },
  sysGuideNodeRulesDesc: {
    ko: "필수안내사항, 고정 문구, 시각 참조처럼 조직이 직접 정한 검토 항목입니다.",
    en: "Review items your organization defines itself, such as required notices, fixed wording, and visual references.",
  },
  sysGuideNodeEducateSageDesc: {
    ko: "실제로 검토했던 사례(원본 자료, 담당자 피드백, 수정된 자료)를 등록하면 AI가 규칙 초안을 만듭니다. 확인하고 시행하면 학습된 규칙으로 다른 규칙과 똑같이 쓰입니다.",
    en: "Add a case you already handled — the original material, your feedback, and the corrected material — and the AI drafts a rule from it. Once you review and activate it, it works like any other rule.",
  },
  sysGuideNodeDictionaryDesc: {
    ko: "쓸 수 없는 표현 목록입니다. 버전 없이 한 벌로 관리되고, 검토에는 그 시점의 목록이 기록됩니다.",
    en: "The list of expressions that may not be used. It has no version chain; each review records the list as it stood.",
  },
  sysGuideNodeScopeDesc: {
    ko: "정책과 규칙을 채널, 구분1, 구분2에 연결합니다. 여기서 연결된 것만 해당 광고 검토에 적용됩니다. 그래프, 맵, 표에서 미게시 초안과 연결 없는 칸이 강조되고, 오른쪽의 ‘연결 없는 노드만’ 확인란을 켜면 아직 연결되지 않은 조합만 볼 수 있습니다.",
    en: "Maps policies and rules onto channels and categories. Only what is mapped here applies to a given review. Unpublished drafts and unconnected cells are highlighted in the graph, map, and table; turn on the Unconnected only checkbox to see combinations that still need a connection.",
  },
  sysGuideNodeAskSageDesc: {
    ko: "자료를 제출하기 전에 표현이나 기준을 물어봅니다. 등록된 사전과 승인된 정책, 규칙에 근거해 답하며, 질문과 답변은 기록됩니다.",
    en: "Ask about a phrase or a standard before submitting. Answers come from the registered dictionary and approved standards, and the conversation is recorded.",
  },
  sysGuideNodeAskSageLogDesc: {
    ko: "구성원이 무엇을 물었고 Sage가 어떻게 답했는지, 어떤 질문에 답하지 못했는지 확인합니다.",
    en: "See what members asked, how Sage answered, and which questions it could not answer.",
  },
  sysGuideNodeReviewsDesc: {
    ko: "광고 문안과 파일을 제출하고 검토 결과를 확인합니다. 관리자는 결정 대기 검토를 확인하고 승인 또는 반려할 수 있습니다.",
    en: "Submit copy and files, then track the outcome. Managers can find reviews needing a decision and approve or reject them here.",
  },
  sysGuideNodeAnalysis: { ko: "자동 분석", en: "Automated analysis" },
  sysGuideNodeAnalysisDesc: {
    ko: "금지 표현과 고정 문구를 글자 그대로 대조한 뒤, AI가 승인된 근거를 인용해 검토합니다. 인용이 확인되지 않은 지적은 버려집니다.",
    en: "Literal checks for forbidden expressions and fixed wording run first; the AI then reviews while citing approved sources. Findings whose citation cannot be verified are discarded.",
  },
  sysGuideNodeDashboardDesc: {
    ko: "조직의 검토 건수, 결정 대기, 고위험 건을 한눈에 봅니다.",
    en: "See your organization's review volume, decisions pending, and high-risk items at a glance.",
  },
  sysGuideNodeConfigDesc: {
    ko: "자동 종결, 품질 샘플링, 처리 목표 시간과 승인 페이지에 인쇄될 서명 문구, 직인을 정합니다. 기본값은 결과를 기록만 하는 섀도 모드입니다.",
    en: "Set automatic clearance, quality sampling, turnaround targets, and the signature text and seal printed on the approval page. The default is shadow mode, which only records outcomes.",
  },
  sysGuideNodeMembersDesc: {
    ko: "구성원과 권한을 관리합니다. 메뉴가 보이지 않는 것은 안내일 뿐이고, 실제 권한은 서버가 판단합니다.",
    en: "Manage members and their roles. A hidden menu is only a hint; the server is what actually enforces permissions.",
  },
  sysGuideNodeQueueHealthDesc: {
    ko: "대기 건수, 최장 대기 시간, 재시도가 소진된 작업처럼 처리 상태를 봅니다.",
    en: "See queue depth, the longest current wait, and jobs that have exhausted their retries.",
  },
  sysGuideNodeAuditDesc: {
    ko: "로그인, 기준 변경, 검토 제출과 결정, 보고서와 승인 문서 다운로드가 기록으로 남습니다.",
    en: "Logins, standard changes, review submissions and decisions, and report and approved-document downloads are all recorded.",
  },
  sysGuideTermsTitle: { ko: "자주 나오는 용어", en: "Terms you will see" },
  sysGuideTermChannel: { ko: "채널", en: "Channel" },
  sysGuideTermChannelDesc: {
    ko: "광고가 나가는 경로입니다. 어떤 기준이 적용될지 정하는 축 가운데 하나입니다.",
    en: "The route the advertising goes out through. One of the two axes that decide which standards apply.",
  },
  sysGuideTermCategory: { ko: "구분1, 구분2", en: "Category tree" },
  sysGuideTermCategoryDesc: {
    ko: "광고 자료의 분류입니다. 구분1을 고르면 그 아래 구분2가 모두 포함되고, 구분2를 고르면 그 항목만 적용됩니다.",
    en: "How material is classified. Choosing a parent includes all of its children; choosing a child is exact.",
  },
  sysGuideTermVersion: { ko: "버전", en: "Version" },
  sysGuideTermVersionDesc: {
    ko: "정책과 규칙은 고쳐 쓰지 않고 새 버전으로 쌓입니다. 승인되고 시행된 버전만 검토에 쓰입니다.",
    en: "Policies and rules are never edited in place; they accumulate as versions. Only approved, effective versions are used.",
  },
  sysGuideTermSnapshot: { ko: "스냅샷", en: "Snapshot" },
  sysGuideTermSnapshotDesc: {
    ko: "검토를 시작할 때 쓴 기준을 그대로 복사해 둔 것입니다. 나중에 기준이 바뀌어도 지난 검토의 근거는 달라지지 않습니다.",
    en: "A copy of the standards used when a review began. Later changes never alter a completed review's evidence.",
  },
  sysGuideTermRevision: { ko: "심의본", en: "Material revision" },
  sysGuideTermRevisionDesc: {
    ko: "한 번 제출된 광고 내용의 고정된 사본입니다. 내용을 고치려면 새 심의본을 제출합니다.",
    en: "The frozen copy of what was submitted. Changing the material means submitting a new revision.",
  },
  sysGuideTermApprovedDocument: { ko: "승인 문서", en: "Approved document" },
  sysGuideTermApprovedDocumentDesc: {
    ko: "승인된 심의본 원본 마지막에 승인 사실, 심의자, 승인 일시를 담은 승인 페이지를 붙인 문서입니다. 승인 시점의 서명 문구가 그대로 보관되므로, 이후 설정을 바꿔도 이미 내려받은 문서의 내용은 달라지지 않습니다.",
    en: "The approved material with a page appended stating that it was approved, by whom, and when. The signature wording is kept as it was at approval, so changing the setting later never alters a document already issued.",
  },
  sysGuideTermTaughtRule: { ko: "학습된 규칙", en: "Taught rule" },
  sysGuideTermTaughtRuleDesc: {
    ko: "Educate Sage에서 실제 사례로 만들어진 규칙입니다. 직접 작성한 규칙과 같은 버전, 승인, 스냅샷 절차를 따르며, 저장 직후에는 초안이라 시행해야 검토에 쓰입니다.",
    en: "A rule created from a real case in Educate Sage. It follows the same version, approval, and snapshot path as a hand-written rule, and starts as a draft that must be activated before reviews use it.",
  },
  sysGuideTermCitation: { ko: "인용", en: "Citation" },
  sysGuideTermCitationDesc: {
    ko: "지적마다 어떤 근거에서 나왔는지 표시합니다. 정책과 규칙, 사전은 완전한 근거이고, 기본 참고자료만 인용한 검토는 자동 종결되지 않으며, 근거 없음은 참고 의견으로만 다룹니다.",
    en: "What each finding is based on. Policies, rules, and the dictionary carry full weight; a review citing only baseline references never auto-clears; findings with no source stay advisory.",
  },
  sysGuideTermDeterministic: { ko: "결정적 검사", en: "Deterministic check" },
  sysGuideTermDeterministicDesc: {
    ko: "AI 이전에 수행하는 기계적 대조입니다. 금지 표현과 고정 문구가 글자 그대로 있는지 확인합니다.",
    en: "The literal comparison that runs before the AI, verifying forbidden expressions and fixed wording word for word.",
  },
  sysGuideTermRouting: { ko: "결과 라우팅", en: "Result routing" },
  sysGuideTermRoutingDesc: {
    ko: "검토 결과를 자동 종결(통과/반려), 조치 필요, 사람 확인 필요, 처리 실패 가운데 하나로 보냅니다.",
    en: "Sends each result to self-check clear, self-check rejected, action required, escalated, or processing failed.",
  },
  sysGuideTermShadowMode: { ko: "섀도 모드", en: "Shadow mode" },
  sysGuideTermShadowModeDesc: {
    ko: "결과를 기록만 하고 자동으로 종결하지는 않는 기본 설정입니다.",
    en: "The default setting: outcomes are recorded but nothing clears automatically.",
  },
  sysGuideTermDecision: { ko: "결정", en: "Decision" },
  sysGuideTermDecisionDesc: {
    ko: "관리자가 남기는 최종 판단으로 승인, 조건부 승인, 반려가 있습니다. 심의본마다 한 번 기록되면 바뀌지 않습니다.",
    en: "An admin's final call: approved, approved with changes, or rejected. Once recorded for a revision it does not change.",
  },
  sysGuideStartTour: { ko: "메뉴 위치 안내 시작", en: "Show me these menus" },
  sysGuideTourStep: { ko: "{current} / {total}", en: "{current} of {total}" },
  sysGuideNext: { ko: "다음", en: "Next" },
  sysGuidePrev: { ko: "이전", en: "Back" },
  sysGuideFinish: { ko: "안내 종료", en: "Finish" },
  sysGuideBackToDiagram: { ko: "전체 그림 다시 보기", en: "Back to the diagram" },
  sysGuideClose: { ko: "닫기", en: "Close" },
  sysGuideLocked: { ko: "권한 없음", en: "No access" },
  sysGuideLockedHint: {
    ko: "현재 권한으로는 이 메뉴를 사용할 수 없습니다.",
    en: "Your current role cannot open this menu.",
  },
  sysGuideAutomatic: { ko: "자동 처리", en: "Automatic" },
  sysGuideTourMissing: {
    ko: "이 메뉴는 지금 화면에 표시되어 있지 않습니다. 권한을 확인해 주세요.",
    en: "This menu is not visible right now. Check your role.",
  },
  sysGuideTourEmpty: {
    ko: "현재 권한으로 안내할 메뉴가 없습니다.",
    en: "There are no menus to walk through with your current role.",
  },

  // System guide pages
  sysGuideTabOverview: { ko: "전체 구조", en: "Overview" },
  sysGuideTabTerms: { ko: "용어", en: "Terms" },
  sysGuidePageOf: { ko: "{current} / {total} 쪽", en: "Page {current} of {total}" },
  sysGuideMindMapTitle: { ko: "메뉴 관계도", en: "How the menus relate" },
  sysGuideMindMapDesc: {
    ko: "메뉴는 세 갈래로 나뉩니다. 상자를 누르면 그 메뉴로 이동해 화면에서 위치를 알려 드립니다.",
    en: "The menus fall into three branches. Select a box to open that menu and see where it sits in the menu.",
  },
  sysGuideSequenceTitle: { ko: "검토 한 건의 처리 흐름", en: "What happens during one review" },
  sysGuideSequenceDesc: {
    ko: "광고를 제출한 순간부터 결정이 기록될 때까지 누가 무엇을 하는지 순서대로 보여 줍니다.",
    en: "Who does what, in order, from the moment material is submitted until a decision is recorded.",
  },
  sysGuideActorRequester: { ko: "요청자", en: "Requester" },
  sysGuideActorEngine: { ko: "검토 엔진", en: "Review engine" },
  sysGuideActorAi: { ko: "VaultSage AI", en: "VaultSage AI" },
  sysGuideActorAdmin: { ko: "컴플라이언스 관리자", en: "Compliance admin" },
  sysGuideSeqSubmit: { ko: "광고 문안, 파일 제출", en: "Submit copy and files" },
  sysGuideSeqFreeze: { ko: "심의본과 적용 기준을 스냅샷으로 고정", en: "Freeze the revision and snapshot the standards" },
  sysGuideSeqDeterministic: { ko: "결정적 검사: 금지 표현, 고정 문구 대조", en: "Deterministic checks: forbidden expressions, fixed wording" },
  sysGuideSeqAsk: { ko: "승인된 근거와 함께 검토 요청", en: "Review request with approved sources" },
  sysGuideSeqFindings: { ko: "지적과 인용을 반환", en: "Return findings with citations" },
  sysGuideSeqVerify: { ko: "인용 검증: 확인되지 않은 지적은 폐기", en: "Verify citations; discard what cannot be traced" },
  sysGuideSeqRoute: { ko: "결과 라우팅 결정", en: "Decide how the result is routed" },
  sysGuideSeqQueue: { ko: "자동 종결 대상이 아니면 대기열로", en: "Anything that does not auto-clear goes to the queue" },
  sysGuideSeqDecide: { ko: "승인, 조건부 승인, 반려 기록", en: "Record approved, approved with changes, or rejected" },
  sysGuideSeqAudit: { ko: "결정과 근거를 감사 기록에 저장", en: "Store the decision and its evidence in the audit log" },
} as const satisfies Record<string, Entry>;
